/* remarkCjkStrong — 파싱 뒤 text 노드에 남은 «**…**» 를 strong 노드로 바꾸는 remark 플러그인.
 *
 * 원인: CommonMark 에서 닫는 `**` 앞 글자가 문장부호(`)`·`"`)이면 뒤 글자가 공백·문장부호일 때만
 * 닫힘으로 인정된다. 한국어는 조사가 바로 붙어(«**OURi(아우리)**입니다») 안 닫히고 별표가 보인다.
 * 라이브러리 결함이 아니라 표준 동작이라 파싱 뒤 남은 글자만 고친다.
 *
 * 🔴 ESM 패키지(unist-util-visit 등) import 금지 — CRA jest 는 node_modules 를 변환하지 않아
 *    이 파일을 끌어오는 시험이 로드 단계에서 깨진다(`SyntaxError: Unexpected token 'export'`).
 *    그래서 mdast 를 직접 순회한다.
 *
 * 바꾸지 않는 것:
 *  - 원문 구간에 `\*` 또는 별표 문자 참조(&#42; 등)가 있는 text 노드 — 작성자가 일부러 쓴 별표다.
 *    노드 단위로 통째로 건너뛴다(같은 노드에 섞인 진짜 닫힘 실패도 그대로 둔다).
 *  - 닫는 `**` 바로 뒤가 ASCII 영숫자인 매치 — `(x**2)**0.5` 같은 거듭제곱 표기.
 *  - 자동 링크 안(url 이 표시 글자를 포함하는 link) · code·inlineCode·html(text 노드가 아님).
 *  - 여러 노드에 걸친 `**`(범위 밖).
 */

const LEFTOVER_STRONG = /\*\*(?=\S)([^*\n]*?\S)\*\*/g;
const LITERAL_STAR = /\\\*|&#0*42;|&#x0*2a;|&ast;|&midast;/i;
const ASCII_ALNUM = /[A-Za-z0-9]/;

function hasLiteralStar(node, source) {
  if (source == null) return false;
  const pos = node.position;
  if (!pos || !pos.start || !pos.end) return false;
  const start = pos.start.offset;
  const end = pos.end.offset;
  if (typeof start !== 'number' || typeof end !== 'number') return false;
  return LITERAL_STAR.test(source.slice(start, end));
}

function splitText(node, source) {
  const value = node.value;
  if (typeof value !== 'string' || value.indexOf('**') === -1) return null;
  if (hasLiteralStar(node, source)) return null;

  const parts = [];
  let last = 0;
  let m;
  LEFTOVER_STRONG.lastIndex = 0;
  while ((m = LEFTOVER_STRONG.exec(value)) !== null) {
    const end = m.index + m[0].length;
    if (ASCII_ALNUM.test(value.charAt(end))) continue;
    if (m.index > last) parts.push({ type: 'text', value: value.slice(last, m.index) });
    parts.push({ type: 'strong', children: [{ type: 'text', value: m[1] }] });
    last = end;
  }
  if (!parts.length) return null;
  if (last < value.length) parts.push({ type: 'text', value: value.slice(last) });
  return parts;
}

function isAutolink(node) {
  if (node.type !== 'link' || typeof node.url !== 'string') return false;
  const kids = node.children;
  return kids.length === 1 && kids[0] && kids[0].type === 'text'
    && typeof kids[0].value === 'string' && node.url.includes(kids[0].value);
}

function walk(node, source) {
  const children = node && node.children;
  if (!Array.isArray(children) || isAutolink(node)) return;
  let next = null;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const parts = child && child.type === 'text' ? splitText(child, source) : null;
    if (parts) {
      if (!next) next = children.slice(0, i);
      next.push(...parts);
    } else {
      walk(child, source);
      if (next) next.push(child);
    }
  }
  if (next) node.children = next;
}

export function strongifyLeftovers(tree, source) {
  walk(tree, source == null ? null : String(source));
  return tree;
}

export default function remarkCjkStrong() {
  return (tree, file) => {
    strongifyLeftovers(tree, file && file.value != null ? String(file.value) : null);
  };
}
